import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="document-query"
export default class extends Controller {
  static targets = ["documentType", "documentNumber", "name", "button", "badge", "phone"]

  connect() {
    if (this.documentNumberTarget.value.trim() !== "") {
      this.search({ silent: true })
    }
  }

  searchPreventDefault(event) {
    event.preventDefault()
    this.search()
  }

  // Al cambiar el documento: desbloquea tipo y limpia el bloqueo previo
  onDocumentNumberChange() {
    this.unlockDocumentType()
  }

  sanitizePhone() {
    if (!this.hasPhoneTarget) return

    const digits = this.phoneTarget.value.replace(/\D/g, "").slice(0, 9)
    this.phoneTarget.value = digits
  }

  async search({ silent = false } = {}) {
    const documentNumber = this.documentNumberTarget.value.trim()

    if (documentNumber === "") {
      return
    }

    const originalButtonText = this.hasButtonTarget ? this.buttonTarget.innerHTML : null
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.innerHTML = '<span class="loading loading-spinner loading-sm"></span>'
    }

    try {
      const response = await fetch(`/sales/customers/query_document?document_number=${encodeURIComponent(documentNumber)}`, {
        headers: { Accept: "application/json" }
      })

      if (!response.ok) {
        console.error("Error al consultar el documento", await response.text())
        if (!silent) this.showToast("No se pudo consultar el documento. Intente de nuevo.", "error")
        return
      }

      const data = await response.json()

      if (data.client_source === "not_found") {
        this.applyNotFound({ clearName: !silent, silent })
        return
      }

      this.applyFound(data, { silent })
    } catch (error) {
      console.error("Network error", error)
      if (!silent) this.showToast("Error de red al consultar el documento.", "error")
    } finally {
      if (this.hasButtonTarget && originalButtonText !== null) {
        this.buttonTarget.disabled = false
        this.buttonTarget.innerHTML = originalButtonText
      }
    }
  }

  applyNotFound({ clearName = true, silent = false } = {}) {
    this.unlockName()
    this.unlockDocumentType()
    if (clearName) this.nameTarget.value = ""
    this.clearBadge()

    if (!silent) {
      this.showToast(
        "No se encontró un cliente con este documento. Ingrese los datos manualmente.",
        "warning"
      )
    }
  }

  applyFound(data, { silent = false } = {}) {
    if (data.name) {
      this.nameTarget.value = data.name
    }

    this.lockName()

    if (data.document_type) {
      const dt = data.document_type.toLowerCase()
      if (["ruc", "dni", "ce", "pasaporte"].includes(dt)) {
        this.documentTypeTarget.value = dt
        this.lockDocumentType()
      } else {
        this.unlockDocumentType()
      }
    } else {
      this.unlockDocumentType()
    }

    this.updateBadge(data)

    if (silent && !data.warning) return

    if (data.warning) {
      this.showToast("Cliente encontrado en ERP, pero está deshabilitado.", "warning")
    } else if (data.client_source === "erp") {
      this.showToast("Cliente encontrado en ERP.", "success")
    } else if (data.client_source === "api") {
      this.showToast("Cliente encontrado en GeoDocs.", "success")
    } else {
      this.showToast("Cliente encontrado.", "success")
    }
  }

  lockName() {
    this.nameTarget.readOnly = true
    this.nameTarget.classList.add("bg-base-200", "text-base-content/70", "cursor-not-allowed")
    this.nameTarget.setAttribute("aria-readonly", "true")
  }

  unlockName() {
    this.nameTarget.readOnly = false
    this.nameTarget.classList.remove("bg-base-200", "text-base-content/70", "cursor-not-allowed")
    this.nameTarget.removeAttribute("aria-readonly")
  }

  lockDocumentType() {
    if (!this.hasDocumentTypeTarget) return

    this.documentTypeTarget.disabled = true
    this.documentTypeTarget.classList.add("bg-base-200", "text-base-content/70", "cursor-not-allowed")
    this.syncDocumentTypeHidden()
  }

  unlockDocumentType() {
    if (!this.hasDocumentTypeTarget) return

    this.documentTypeTarget.disabled = false
    this.documentTypeTarget.classList.remove("bg-base-200", "text-base-content/70", "cursor-not-allowed")
    this.removeDocumentTypeHidden()
  }

  // Un <select disabled> no se envía en el form; usamos un hidden con el mismo name
  syncDocumentTypeHidden() {
    let hidden = this.element.querySelector("[data-document-query-type-hidden]")
    if (!hidden) {
      hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.dataset.documentQueryTypeHidden = "true"
      hidden.name = this.documentTypeTarget.name
      this.documentTypeTarget.insertAdjacentElement("afterend", hidden)
    }
    hidden.value = this.documentTypeTarget.value
  }

  removeDocumentTypeHidden() {
    const hidden = this.element.querySelector("[data-document-query-type-hidden]")
    if (hidden) hidden.remove()
  }

  updateBadge(data) {
    if (!this.hasBadgeTarget) return

    this.clearBadge()
    this.badgeTarget.classList.remove("hidden")

    if (data.client_source === "erp") {
      if (data.warning) {
        this.badgeTarget.classList.add("badge-warning")
        this.badgeTarget.textContent = `ERP (${data.erp_correlative}) · Deshabilitado`
      } else {
        this.badgeTarget.classList.add("badge-primary")
        this.badgeTarget.textContent = `Cliente en ERP (${data.erp_correlative})`
      }
    } else if (data.client_source === "api") {
      this.badgeTarget.classList.add("badge-accent")
      this.badgeTarget.textContent = "Aún no existe en ERP · GeoDocs"
    }
  }

  clearBadge() {
    if (!this.hasBadgeTarget) return

    this.badgeTarget.classList.add("hidden")
    this.badgeTarget.classList.remove("badge-primary", "badge-accent", "badge-warning", "badge-error")
    this.badgeTarget.textContent = ""
  }

  showToast(message, type = "info") {
    let container = document.querySelector('[data-controller="shared--flash-messages"]')

    if (!container) {
      container = document.createElement("div")
      container.dataset.controller = "shared--flash-messages"
      container.className = "fixed top-4 right-4 space-y-2 z-50 w-80"
      document.body.appendChild(container)
    }

    const alertClass = {
      success: "alert-success",
      error: "alert-error",
      warning: "alert-warning",
      info: "alert-info"
    }[type] || "alert-info"

    const iconPath = {
      success: '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>',
      error: '<circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/>',
      warning: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
      info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>'
    }[type] || '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>'

    const el = document.createElement("div")
    el.setAttribute("role", "alert")
    el.className = `alert ${alertClass} alert-soft border shadow-md font-medium transition ease-in-out duration-500 opacity-0`
    el.dataset.sharedFlashMessagesTarget = "message"

    el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5 shrink-0">${iconPath}</svg>`

    const span = document.createElement("span")
    span.textContent = message
    el.appendChild(span)
    container.appendChild(el)

    requestAnimationFrame(() => {
      el.classList.remove("opacity-0")
      el.classList.add("opacity-100")
    })

    const duration = type === "warning" ? 6000 : 4000
    setTimeout(() => {
      el.classList.remove("opacity-100")
      el.classList.add("opacity-0")
      setTimeout(() => el.remove(), 500)
    }, duration)
  }
}
